import { useEffect, useState } from "react";
import { Leaf, WifiOff, CloudOff } from "lucide-react";
import { useApp } from "@/context/AppContext";

export const ECO_LINES = [
  "Hang on, the Earth is taking a deep breath.",
  "Please give the Earth a moment to regain its strength.",
  "Like a seed, good things take a little time to grow.",
  "Your scrap is safe. We're sorting things out, just like you sort your waste.",
  "Even the internet needs a green break sometimes.",
  "Fun fact: one recycled can saves enough energy to run a TV for 3 hours.",
  "Every kilo you recycle gives the planet a little more room to breathe.",
  "Trees don't rush, and they still change the world.",
];

const LANDS = [
  "M8 30q8-8 18-4q6 6 2 14q-8 4-6 12q-4 8-12 4q-6-10-2-26z",
  "M45 20q10-4 18 2q4 8-4 12q-10 2-8 10q2 10-6 14q-8-4-6-16q-6-10 6-22z",
  "M72 55q10-2 16 6q2 10-8 14q-10-2-8-20z",
  "M20 66q8 0 10 8q-2 8-10 8q-6-6 0-16z",
  "M80 18q8 2 10 8q-6 4-12 0z",
];
const Lands = () => LANDS.map((d) => <path key={d} d={d} fill="#2E9E6B" />);

export const SpinningEarth = ({ size = 160 }) => (
  <div className="relative" style={{ width: size, height: size }} data-testid="spinning-earth">
    <div className="absolute inset-0 rounded-full bg-[#2E9E6B]/20 blur-2xl ks-earth-glow" />
    <svg viewBox="0 0 100 100" className="relative h-full w-full drop-shadow-xl">
      <defs>
        <clipPath id="ks-globe"><circle cx="50" cy="50" r="46" /></clipPath>
        <radialGradient id="ks-shade" cx="35%" cy="30%" r="75%">
          <stop offset="0%" stopColor="#fff" stopOpacity=".35" /><stop offset="60%" stopColor="#fff" stopOpacity="0" /><stop offset="100%" stopColor="#0D3B2B" stopOpacity=".45" />
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="46" fill="#3B8FB8" />
      <g clipPath="url(#ks-globe)">
        <g className="ks-earth-land"><Lands /><g transform="translate(100 0)"><Lands /></g></g>
        <g className="ks-earth-clouds" opacity=".7"><ellipse cx="30" cy="45" rx="12" ry="2.5" fill="#fff" /><ellipse cx="70" cy="72" rx="10" ry="2" fill="#fff" />
          <ellipse cx="130" cy="45" rx="12" ry="2.5" fill="#fff" /><ellipse cx="170" cy="72" rx="10" ry="2" fill="#fff" /></g>
      </g>
      <circle cx="50" cy="50" r="46" fill="url(#ks-shade)" />
    </svg>
    <div className="absolute inset-[-14px] ks-orbit"><Leaf className="absolute left-1/2 top-0 h-6 w-6 -translate-x-1/2 fill-[#2E9E6B]/30 text-[#2E9E6B]" /></div>
  </div>
);

export const useRotating = (lines = ECO_LINES, ms = 4000) => {
  const [i, setI] = useState(() => Math.floor(Math.random() * lines.length));
  useEffect(() => { const id = setInterval(() => setI((x) => (x + 1) % lines.length), ms); return () => clearInterval(id); }, [lines, ms]);
  return lines[i];
};

const FLOAT = [["8%", "18%", 0], ["86%", "12%", 1.2], ["14%", "72%", 2.1], ["80%", "64%", .6], ["50%", "88%", 1.7], ["92%", "40%", 2.6]];
const BADGE = { offline: [WifiOff, "No internet connection"], down: [CloudOff, "Kindsaver servers resting"] };

export const EarthScreen = ({ title, sub, children, testid = "earth-screen", overlay, badge }) => {
  const { t } = useApp();
  const line = useRotating();
  const B = BADGE[badge];
  const Icon = B?.[0];
  return (
    <div data-testid={testid} className={`${overlay ? "fixed inset-0 z-[2000]" : "min-h-screen"} flex flex-col items-center justify-center gap-6 overflow-hidden bg-[radial-gradient(60%_40%_at_50%_38%,rgba(46,158,107,.18),transparent_70%),linear-gradient(180deg,#F4F8F5,#E6F1EA)] px-6 text-center`}>
      {FLOAT.map(([l, tp, d]) => <Leaf key={l + tp} className="ks-float pointer-events-none absolute h-5 w-5 fill-[#2E9E6B]/20 text-[#2E9E6B]/50" style={{ left: l, top: tp, animationDelay: `${d}s` }} />)}
      <img src="/logo-full.png" alt="Kindsaver" className="ks-rise h-9 w-auto" data-testid="earth-logo" />
      <div className="relative grid place-items-center">
        <span className="ks-ring absolute h-56 w-56 rounded-full border-2 border-[#2E9E6B]/25" />
        <span className="ks-ring absolute h-56 w-56 rounded-full border-2 border-[#2E9E6B]/25" style={{ animationDelay: "1.3s" }} />
        <SpinningEarth size={170} />
      </div>
      <div className={`ks-rise max-w-sm space-y-2 ${B ? "rounded-[28px] border border-[#DCE8E0] bg-white/75 p-5 shadow-[0_20px_50px_-30px_rgba(13,59,43,.5)] backdrop-blur-md" : ""}`}>
        {B && <span className="inline-flex items-center gap-2 rounded-full bg-[#FDEEE8] px-3 py-1 text-xs font-bold text-[#B4401F]" data-testid="earth-badge"><Icon className="h-3.5 w-3.5" />{t(B[1])}<i className="h-2 w-2 animate-pulse rounded-full bg-[#E05C3A]" /></span>}
        {title && <h1 className="font-display text-2xl font-extrabold text-[#0D3B2B]">{t(title)}</h1>}
        {sub && <p className="text-sm text-[#4A6358]">{t(sub)}</p>}
        <p key={line} className="ks-rise min-h-[40px] text-sm font-semibold italic text-[#2E9E6B]" data-testid="earth-line">“{t(line)}”</p>
      </div>
      {children}
    </div>
  );
};
